import { getToken } from '../api/client';
import { urlApi } from '../api/config';
import { dataUrlEnBlob, envoyerPhotos } from './photos';

/**
 * File d'attente hors ligne.
 *
 * Sur le parking, le reseau tombe. L'agent doit pouvoir finir son etat des lieux
 * quand meme : la saisie part dans IndexedDB, et repart toute seule des que la
 * connexion revient. Les photos y sont stockees en data: URL, puis envoyees au
 * moment de la synchronisation, avant de rejouer la requete.
 */
const BASE = 'friends-hors-ligne';
const STORE = 'file';
/** Brouillons d'assistants en cours : survivent au rechargement de la page. */
const BROUILLONS = 'brouillons';

export interface EnAttente {
  id: string;
  chemin: string;
  corps: Record<string, unknown>;
  libelle: string;
  creeLe: number;
  essais: number;
  erreur?: string;
  /** Le serveur a refuse la saisie. Elle reste en base : c'est a un humain de trancher. */
  rejete?: boolean;
  /** Le jeton a expire pendant que l'appareil etait hors ligne. */
  reconnexion?: boolean;
}

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(BASE, 2);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(BROUILLONS)) db.createObjectStore(BROUILLONS, { keyPath: 'cle' });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function txStore<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return ouvrir().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

/**
 * Brouillon d'assistant.
 *
 * Un etat des lieux, c'est cinq etapes, six photos et deux signatures prises
 * sur un parking, au telephone. Un appel entrant, un onglet recycle par iOS,
 * un rechargement : tout etait perdu, sans avertissement. Le brouillon est
 * ecrit a chaque changement et efface a l'envoi.
 */
export interface Brouillon<T = unknown> { cle: string; donnees: T; majLe: number }

export const enregistrerBrouillon = <T,>(cle: string, donnees: T): Promise<unknown> =>
  txStore(BROUILLONS, 'readwrite', (s) => s.put({ cle, donnees, majLe: Date.now() }));

export const lireBrouillon = <T,>(cle: string): Promise<Brouillon<T> | undefined> =>
  txStore<Brouillon<T> | undefined>(BROUILLONS, 'readonly', (s) => s.get(cle) as IDBRequest<Brouillon<T> | undefined>);

export const effacerBrouillon = (cle: string): Promise<unknown> =>
  txStore(BROUILLONS, 'readwrite', (s) => s.delete(cle));

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await ouvrir();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  });
}

export const listerEnAttente = (): Promise<EnAttente[]> =>
  tx<EnAttente[]>('readonly', (s) => s.getAll() as IDBRequest<EnAttente[]>);

export async function mettreEnFile(chemin: string, corps: Record<string, unknown>, libelle: string): Promise<EnAttente> {
  const item: EnAttente = { id: crypto.randomUUID(), chemin, corps, libelle, creeLe: Date.now(), essais: 0 };
  await tx('readwrite', (s) => s.put(item));
  previenir();
  return item;
}

const retirer = (id: string) => tx('readwrite', (s) => s.delete(id));

/**
 * Rejoue la file dans l'ordre. Rend le nombre d'elements passes.
 *
 * Regle unique : on ne supprime QUE ce que le serveur a accepte. Une saisie
 * refusee reste en base, en rouge, avec le message du serveur — un etat des
 * lieux, c'est six photos et deux signatures prises sur un parking : ca ne
 * s'efface jamais tout seul, meme quand la requete ne passera plus.
 */
export async function synchroniser(): Promise<{ envoyes: number; rejetes: number; restants: number }> {
  const total = async () => (await listerEnAttente()).length;
  if (!navigator.onLine) return { envoyes: 0, rejetes: 0, restants: await total() };
  const items = (await listerEnAttente()).sort((a, b) => a.creeLe - b.creeLe);
  const token = getToken();
  let envoyes = 0;
  let rejetes = 0;

  for (const item of items) {
    try {
      const corps = { ...item.corps };

      // Les photos ont ete gardees en data: URL : on les televerse d'abord.
      const photos = corps.photos;
      if (Array.isArray(photos) && photos.some((p) => typeof p === 'string' && p.startsWith('data:'))) {
        const aEnvoyer = photos.filter((p): p is string => typeof p === 'string' && p.startsWith('data:'));
        const dejaEnLigne = photos.filter((p): p is string => typeof p === 'string' && !p.startsWith('data:'));
        const blobs = await Promise.all(aEnvoyer.map(dataUrlEnBlob));
        corps.photos = [...dejaEnLigne, ...(await envoyerPhotos(blobs, token))];
      }

      const r = await fetch(urlApi(`/api${item.chemin}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(corps),
      });
      if (r.ok) { await retirer(item.id); envoyes++; continue; }

      // Jeton expire pendant la nuit : la saisie est intacte, c'est la session
      // qui ne l'est plus. On garde tout et on arrete la : rien ne sert de
      // rejouer le reste avec le meme jeton mort.
      if (r.status === 401 || r.status === 403) {
        const erreur = r.status === 401
          ? 'Session expiree — reconnectez-vous, la saisie repartira ensuite.'
          : await messageServeur(r);
        await tx('readwrite', (s) => s.put({ ...item, essais: item.essais + 1, reconnexion: r.status === 401, erreur }));
        break;
      }

      // Autre refus du serveur : la requete ne passera pas telle quelle, mais
      // la saisie a de la valeur. On la marque en rouge et on la garde.
      if (r.status >= 400 && r.status < 500) {
        const erreur = await messageServeur(r);
        await tx('readwrite', (s) => s.put({ ...item, essais: item.essais + 1, rejete: true, erreur }));
        rejetes++;
        continue;
      }

      throw new Error(`HTTP ${r.status}`);
    } catch (e) {
      await tx('readwrite', (s) => s.put({ ...item, essais: item.essais + 1, erreur: String(e) }));
      break;   // reseau instable : on retentera au prochain passage
    }
  }
  previenir();
  return { envoyes, rejetes, restants: await total() };
}

/** Le message du serveur vaut mieux qu'un code HTTP pour quelqu'un au comptoir. */
async function messageServeur(r: Response): Promise<string> {
  try {
    const d = await r.json() as { erreur?: string; message?: string };
    return d.erreur ?? d.message ?? `Refus du serveur (${r.status})`;
  } catch { return `Refus du serveur (${r.status})`; }
}

/** Une saisie refusee que l'agent choisit d'abandonner, en connaissance de cause. */
export const abandonner = (id: string): Promise<void> =>
  retirer(id).then(previenir);

/* --- Notification aux ecrans --- */
type Abonne = (n: number) => void;
const abonnes = new Set<Abonne>();
export function surFileChangee(f: Abonne): () => void {
  abonnes.add(f);
  void listerEnAttente().then((l) => f(l.length));
  return () => { abonnes.delete(f); };
}
async function previenir(): Promise<void> {
  const n = (await listerEnAttente()).length;
  abonnes.forEach((f) => f(n));
}

export function demarrerSynchronisation(): void {
  void synchroniser();
  window.addEventListener('online', () => { void synchroniser(); });
  setInterval(() => { if (navigator.onLine) void synchroniser(); }, 60_000);
}
